import * as customer from './customer';
import { CustomerType, } from './types';
// TODO check readonly & optional fields
// code-review: use null as default for the customer selection?
// code-review: not sure the selection of a customer should be part of business logic
export class CustomerAccount {
    /**
     * Creates an instance of CustomerAccount.
     * @param {CustomerAccountResponse} customerAccountResponse Response of user µService
     * @param {number} [selectedCustomerId] Customer id of the selected customer.
     * @param {FlsCustomerGroupResponse} [flsCustomerGroupResponse] Response of group customer µService
     *
     * @memberOf CustomerAccount
     */
    constructor(customerAccountResponse, selectedCustomerId, flsCustomerGroupResponse, authType) {
        this.customerAccountResponse = customerAccountResponse;
        this.flsGroupCustomers =
            flsCustomerGroupResponse &&
                flsCustomerGroupResponse._embedded &&
                flsCustomerGroupResponse._embedded['cg:customers'];
        this.authType = authType ? authType : 'explicit';
        if (!selectedCustomerId) {
            if (customerAccountResponse._embedded) {
                if (customerAccountResponse._embedded['us:fls-default-customer']) {
                    selectedCustomerId =
                        customerAccountResponse._embedded['us:fls-default-customer'].customerId;
                }
                else if (customerAccountResponse._embedded['us:mcs-default-customer']) {
                    selectedCustomerId =
                        customerAccountResponse._embedded['us:mcs-default-customer'].customerId;
                }
            }
        }
        if (customerAccountResponse.oneCDB) {
            if (this.flsDefaultCustomer) {
                this.selectCustomer(this.flsDefaultCustomer.customerId);
            }
            else if (this.mcsDefaultCustomer) {
                this.selectCustomer(this.mcsDefaultCustomer.customerId);
            }
            else {
                this.selectCustomer(undefined);
            }
        }
        else {
            this.selectCustomer(selectedCustomerId);
        }
    }
    static buildCustomerAccount(customerAccountResponse, authType) {
        return new CustomerAccount(customerAccountResponse, undefined, undefined, authType);
    }
    get uniqueId() {
        return this.globalId || this.getUserMobile() || `${Date.now()}`;
    }
    get globalId() {
        return this.customerAccountResponse.globalId;
    }
    get staySignedIn() {
        return this.customerAccountResponse.staySignedIn;
    }
    get status() {
        return this.customerAccountResponse.status;
    }
    get lastName() {
        return this.customerAccountResponse.lastName;
    }
    get firstName() {
        return this.customerAccountResponse.firstName;
    }
    get login() {
        return this.customerAccountResponse.login;
    }
    get mobile() {
        return this.customerAccountResponse.mobile;
    }
    get title() {
        return this.customerAccountResponse.title;
    }
    get email() {
        return this.customerAccountResponse.email;
    }
    get birthDate() {
        return this.customerAccountResponse.birthDate;
    }
    get preferredLanguage() {
        return this.customerAccountResponse.preferredLanguage;
    }
    get authenticationType() {
        return this.authType;
    }
    /**
     * Flag whether the user has one customer number for any product he has, both FLS and MCS.
     */
    get oneCDB() {
        return this.customerAccountResponse.oneCDB;
    }
    get flsDefaultCustomer() {
        var _a;
        return (_a = this.customerAccountResponse._embedded) === null || _a === void 0 ? void 0 : _a['us:fls-default-customer'];
    }
    get mcsDefaultCustomer() {
        var _a;
        return (_a = this.customerAccountResponse._embedded) === null || _a === void 0 ? void 0 : _a['us:mcs-default-customer'];
    }
    get implicitlyLoggedCustomer() {
        var _a;
        return (_a = this.customerAccountResponse._embedded) === null || _a === void 0 ? void 0 : _a['us:implicit'];
    }
    get prime() {
        var _a, _b, _c, _d;
        if (this.flsDefaultCustomer) {
            return !!((_b = (_a = this.customerAccountResponse._embedded) === null || _a === void 0 ? void 0 : _a['us:fls-default-customer']) === null || _b === void 0 ? void 0 : _b.prime);
        }
        if (this.mcsDefaultCustomer) {
            return !!((_d = (_c = this.customerAccountResponse._embedded) === null || _c === void 0 ? void 0 : _c['us:mcs-default-customer']) === null || _d === void 0 ? void 0 : _d.prime);
        }
        return false;
    }
    get emailConfirmed() {
        return this.customerAccountResponse.emailConfirmed;
    }
    isGuest() {
        return (!this.customerAccountResponse._embedded ||
            Object.keys(this.customerAccountResponse._embedded).length === 0);
    }
    isUr() {
        if (!!this.getSelectedCustomer()) {
            return (!this.isGuest() &&
                !this.getSelectedCustomer().payer &&
                !this.getSelectedCustomer().subsegment.match(/PRE/));
        }
        return false;
    }
    isPrepaid() {
        if (!!this.getSelectedCustomer()) {
            return !this.isGuest() && !!this.getSelectedCustomer().subsegment.match(/PRE/);
        }
        return false;
    }
    isRES() {
        if (this.getSelectedCustomer()) {
            const subsegment = this.getSelectedCustomer().subsegment;
            if (subsegment && subsegment.match(/11|12|13|RES/)) {
                return true;
            }
        }
        return false;
    }
    isCOR() {
        if (this.getSelectedCustomer()) {
            const subsegment = this.getSelectedCustomer().subsegment;
            if (subsegment && subsegment.match(/22|23|31|32|COR/)) {
                return true;
            }
        }
        return false;
    }
    isSE() {
        if (this.getSelectedCustomer()) {
            const subsegment = this.getSelectedCustomer().subsegment;
            if (subsegment && subsegment.match(/24|25|SE|ME|BUS/)) {
                return true;
            }
        }
        return false;
    }
    isEBU() {
        var _a, _b;
        if (this.oneCDB === undefined && this.implicitlyLoggedCustomer) {
            return (((_b = (_a = this.implicitlyLoggedCustomer) === null || _a === void 0 ? void 0 : _a.provider) === null || _b === void 0 ? void 0 : _b.toString().toLowerCase()) ===
                CustomerType.MCS.toString() && !this.implicitlyLoggedCustomer.prepaid);
        }
        return !!this.getSelectedCustomer() && this.oneCDB === false;
    }
    isEBUPMIT() {
        // EBU can be FLS (EBU PMIT) or MCS (EBU LEGACY): no more need to check the customer provider value
        // oneCDB is undefined for implicit | oneCdb is false for explicit
        if (!!this.getSelectedCustomer() &&
            (this.oneCDB === undefined || !this.oneCDB) &&
            !this.getSelectedCustomer().prepaid) {
            const segment = this.getSegment();
            // only specific segments are considered as EBU
            if (segment && segment.match(/SE|COR/)) {
                return true;
            }
        }
        return false;
    }
    isPrime() {
        return !!this.prime;
    }
    getSelectedCustomer() {
        return this.selectedCustomer;
    }
    getOwnedMsisdns() {
        var _a;
        return (_a = this.customerAccountResponse.ownedMsisdns) !== null && _a !== void 0 ? _a : [];
    }
    getSubscriptionType() {
        if (this.getSelectedCustomer()) {
            if (this.getSelectedCustomer().subscriptionType) {
                const subscriptionType = this.getSelectedCustomer().subscriptionType;
                if (subscriptionType === 'autoreload') {
                    if (this.isUr()) {
                        return 'prepaid';
                    }
                    return 'postpaid';
                }
                return subscriptionType === 'prepaid' ? 'prepaid' : 'postpaid';
            }
            if (this.getSelectedCustomer().prepaid) {
                return this.getSelectedCustomer().prepaid ? 'prepaid' : 'postpaid';
            }
        }
        return 'postpaid';
    }
    getUserMobile() {
        return this.mobile ? this.mobile : this.getMsisdnForImplicit();
    }
    getMsisdnForImplicit() {
        if (this.customerAccountResponse._embedded &&
            this.customerAccountResponse._embedded['us:implicit'] &&
            this.customerAccountResponse._embedded['us:implicit'].msisdn) {
            return this.customerAccountResponse._embedded['us:implicit'].msisdn;
        }
        return '';
    }
    getSegment() {
        if (this.getSelectedCustomer()) {
            const subsegment = this.getSelectedCustomer().subsegment;
            if (subsegment.match(/11|12|13|RES/)) {
                return 'RES';
            }
            if (subsegment.match(/PRE/)) {
                return 'PRE';
            }
            if (subsegment.match(/22|23|31|32|COR/)) {
                return 'COR';
            }
            if (subsegment.match(/24|25|SE|ME|BUS/)) {
                return 'SE';
            }
        }
        return '';
    }
    /**
     * Selects a customer having provided customerId.
     * Note: As customerId may identify fls & mcs customer, fls will always have precendence.
     * @param {number} customerId Identifies the customer to be selected.
     * @returns {boolean} True if the customer is found.
     *
     * @memberOf CustomerAccount
     */
    selectCustomer(customerId) {
        var _a, _b;
        let isCustomerFound = false;
        if (customerId) {
            if (this.flsDefaultCustomer && customerId === this.flsDefaultCustomer.customerId) {
                this.selectedCustomer = this.createFLSCustomer();
                isCustomerFound = true;
            }
            else if (this.mcsDefaultCustomer && customerId === this.mcsDefaultCustomer.customerId) {
                this.selectedCustomer = this.createMCSCustomer();
                isCustomerFound = true;
            }
            else if (this.flsGroupCustomers !== undefined) {
                const selectedFlsGroupCustomer = this.flsGroupCustomers.filter(flsGroupCustomer => customerId === flsGroupCustomer.customerId)[0];
                if (selectedFlsGroupCustomer) {
                    isCustomerFound = true;
                    this.selectedCustomer = new customer.Customer(selectedFlsGroupCustomer.customerId, CustomerType.FLS, selectedFlsGroupCustomer.name);
                }
                else {
                    console.info(`CustomerAccount.selectCustomer: no customer found with provided customerId: ${customerId}`);
                }
            }
            else {
                console.info(`CustomerAccount.selectCustomer: no customer found with provided customerId: ${customerId}`);
            }
        }
        else if (this.flsDefaultCustomer) {
            this.selectedCustomer = this.createFLSCustomer();
            isCustomerFound = true;
        }
        else if (this.mcsDefaultCustomer) {
            this.selectedCustomer = this.createMCSCustomer();
            isCustomerFound = true;
        }
        else if (this.implicitlyLoggedCustomer) {
            this.selectedCustomer = new customer.Customer(undefined, this.implicitlyLoggedCustomer.provider === CustomerType.FLS
                ? CustomerType.FLS
                : CustomerType.MCS, null, this.implicitlyLoggedCustomer.subSegment, undefined, '0' + ((_b = (_a = this.implicitlyLoggedCustomer) === null || _a === void 0 ? void 0 : _a.msisdn) === null || _b === void 0 ? void 0 : _b.slice(2)), undefined, this.implicitlyLoggedCustomer.subscriptionType, undefined, undefined, this.implicitlyLoggedCustomer.prepaid, true);
            isCustomerFound = true;
        }
        return isCustomerFound;
    }
    createMCSCustomer() {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return new customer.Customer((_a = this.mcsDefaultCustomer) === null || _a === void 0 ? void 0 : _a.customerId, CustomerType.MCS, '', (_b = this.mcsDefaultCustomer) === null || _b === void 0 ? void 0 : _b.subsegment, (_c = this.mcsDefaultCustomer) === null || _c === void 0 ? void 0 : _c.payer, (_d = this.mcsDefaultCustomer) === null || _d === void 0 ? void 0 : _d.contactNumber, (_e = this.mcsDefaultCustomer) === null || _e === void 0 ? void 0 : _e.myBusinessComponent, (_f = this.mcsDefaultCustomer) === null || _f === void 0 ? void 0 : _f.subscriptionType, (_g = this.mcsDefaultCustomer) === null || _g === void 0 ? void 0 : _g.customerAccountNumber, (_h = this.mcsDefaultCustomer) === null || _h === void 0 ? void 0 : _h.mcsSubsegment);
    }
    createFLSCustomer() {
        var _a, _b, _c, _d, _e;
        return new customer.Customer((_a = this.flsDefaultCustomer) === null || _a === void 0 ? void 0 : _a.customerId, CustomerType.FLS, (_c = (_b = this.flsDefaultCustomer) === null || _b === void 0 ? void 0 : _b.name) !== null && _c !== void 0 ? _c : '', (_d = this.flsDefaultCustomer) === null || _d === void 0 ? void 0 : _d.subsegment, (_e = this.flsDefaultCustomer) === null || _e === void 0 ? void 0 : _e.payer);
    }
}
